import { useSEO } from "@/hooks/useSEO";
import PageHero from "@/components/PageHero";

const Sec = ({ title, blocks }) => (
  <div className="mt-10">
    <h2 className="font-display text-xl font-semibold text-white">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-mist">
      {blocks.map((b, i) =>
        b.ul ? (
          <ul key={i} className="list-disc space-y-1.5 pl-5">
            {b.ul.map((it) => (
              <li key={it}>{it}</li>
            ))}
          </ul>
        ) : (
          <p key={i}>{b.p}</p>
        )
      )}
    </div>
  </div>
);

const PRIVACY_INTRO = [
  'Synergy Outsourcing Pty Ltd ("Synergy Outsourcing", "Synergy", "we", "us" or "our") respects your privacy and is committed to protecting the personal information we collect and hold.',
  "This Privacy Policy explains how we collect, use, disclose, store and protect personal information when you use our websites, contact us, apply for a role, engage our services, work with us, or otherwise interact with Synergy Outsourcing.",
  "Synergy Outsourcing operates from Australia and works with clients, candidates, contractors, service providers and team members in Australia and internationally.",
  "We aim to handle personal information in accordance with the Privacy Act 1988 (Cth), the Australian Privacy Principles (APPs) and other applicable privacy laws.",
  "By using our website or providing personal information to us, you acknowledge the practices described in this Privacy Policy.",
];

const PRIVACY_SECTIONS = [
  {
    title: "1. About Synergy Outsourcing",
    blocks: [
      { p: "Synergy Outsourcing provides business outsourcing, recruitment, administration, customer support, back-office, sales support and related business services." },
      { p: "Depending on the service provided, we may act as:" },
      { ul: [
        "a service provider to businesses;",
        "a recruitment or talent services provider;",
        "an employer or prospective employer;",
        "a contractor or subcontractor;",
        "a provider of administrative or customer support services; or",
        "a platform through which individuals can apply for employment or engagement opportunities.",
      ]},
      { p: "Our services may involve the use of service providers, contractors and team members located in Australia and other countries." },
    ],
  },
  {
    title: "2. Personal Information We Collect",
    blocks: [
      { p: "The type of personal information we collect depends on how you interact with us." },
      { p: "Information from clients and prospective clients" },
      { p: "If you enquire about or purchase our services, we may collect:" },
      { ul: [
        "name and job title;",
        "company or organisation name;",
        "business contact details;",
        "email address and telephone number;",
        "business address;",
        "information about your business and requirements;",
        "information about the services you are interested in;",
        "communications and correspondence with us;",
        "billing and payment information;",
        "information relating to contracts and service delivery; and",
        "other information you voluntarily provide to us.",
      ]},
      { p: "Information from candidates and job applicants" },
      { p: "If you apply for a position, submit your resume, register with us or otherwise seek employment or contract opportunities, we may collect:" },
      { ul: [
        "name and contact details;",
        "residential or mailing address;",
        "resume or CV;",
        "employment history;",
        "education and qualifications;",
        "professional licences and certifications;",
        "skills and experience;",
        "work rights and eligibility information;",
        "availability;",
        "preferred employment arrangements;",
        "salary or remuneration expectations;",
        "references and referee information;",
        "interview notes and assessments;",
        "information provided during the recruitment process;",
        "information obtained from referees or other legitimate recruitment sources; and",
        "other information you choose to provide.",
      ]},
      { p: "We may also collect information relating to your interactions with us, including applications, interviews, emails, telephone calls and other communications." },
      { p: "Sensitive information" },
      { p: "In limited circumstances, we may collect sensitive information where it is reasonably necessary for our activities and where permitted or required by law." },
      { p: "Sensitive information may include information relating to:" },
      { ul: [
        "health or disability;",
        "racial or ethnic origin;",
        "religious or philosophical beliefs;",
        "membership of professional or trade associations;",
        "criminal record information; or",
        "other categories of information treated as sensitive under applicable privacy law.",
      ]},
      { p: "We will only collect, use or disclose sensitive information in accordance with applicable privacy laws and, where required, with your consent." },
      { p: "You should not provide sensitive information to us unless it is reasonably necessary for the relevant purpose or we have requested it." },
    ],
  },
  {
    title: "3. How We Collect Personal Information",
    blocks: [
      { p: "We may collect personal information directly from you or from other sources." },
      { p: "This may include information collected through:" },
      { ul: [
        "our websites;",
        "online enquiry forms;",
        "job application forms;",
        "candidate registration forms;",
        "email;",
        "telephone calls;",
        "video interviews;",
        "meetings;",
        "social media;",
        "professional networking platforms;",
        "recruitment platforms and job boards;",
        "referrals;",
        "business development activities;",
        "client organisations;",
        "referees;",
        "publicly available sources;",
        "service providers; and",
        "other lawful sources.",
      ]},
      { p: "Where reasonably practicable, we will collect personal information directly from the individual to whom it relates." },
      { p: "In some circumstances, we may collect information from another person or organisation where permitted by law or where reasonably necessary for the relevant business or recruitment activity." },
    ],
  },
  {
    title: "4. Why We Collect Personal Information",
    blocks: [
      { p: "We collect, hold, use and disclose personal information for purposes including:" },
      { ul: [
        "providing our services;",
        "responding to enquiries;",
        "communicating with clients, candidates and prospective clients;",
        "providing recruitment and talent services;",
        "assessing and matching candidates with suitable opportunities;",
        "arranging interviews;",
        "conducting recruitment activities;",
        "conducting reference checks where authorised;",
        "providing candidate information to prospective employers where appropriate;",
        "managing client relationships;",
        "managing contracts and service delivery;",
        "processing payments and invoices;",
        "managing our employees and contractors;",
        "maintaining business records;",
        "improving our services and website;",
        "managing security and preventing fraud or misuse;",
        "complying with legal and regulatory obligations;",
        "resolving disputes and complaints;",
        "conducting business analysis and reporting;",
        "sending relevant business communications and marketing where permitted; and",
        "other purposes that are reasonably necessary for our business or that you have authorised.",
      ]},
      { p: "We will generally only use or disclose personal information for the purpose for which it was collected, a related purpose that you would reasonably expect, where you have consented, or where otherwise permitted or required by law." },
    ],
  },
  {
    title: "5. Recruitment and Candidate Information",
    blocks: [
      { p: "Where you provide your information for recruitment or employment purposes, we may use that information to:" },
      { ul: [
        "assess your suitability for current or future opportunities;",
        "identify roles that may be relevant to your experience;",
        "contact you about employment or contract opportunities;",
        "provide your information to prospective employers or clients where appropriate;",
        "arrange interviews;",
        "conduct reference checks where authorised;",
        "verify qualifications, licences or work rights where reasonably necessary;",
        "communicate the outcome of recruitment processes; and",
        "maintain a candidate database for future opportunities where permitted.",
      ]},
      { p: "Submitting an application does not guarantee an interview, employment, contract engagement or placement." },
      { p: "Where we provide candidate information to a prospective employer or client, we will do so in accordance with applicable privacy laws and our relationship with the candidate." },
    ],
  },
  {
    title: "6. Information We Collect About Website Users",
    blocks: [
      { p: "When you visit our website, we may automatically collect certain technical information, including:" },
      { ul: [
        "IP address;",
        "browser type;",
        "device type;",
        "operating system;",
        "pages visited;",
        "referring website;",
        "approximate geographic information;",
        "date and time of visits;",
        "website interactions; and",
        "other technical or usage information.",
      ]},
      { p: "We may use this information to operate, secure, maintain and improve our website, understand how visitors use our website, and identify technical or security issues." },
      { p: "Where possible, this information will be used in aggregated or de-identified form." },
    ],
  },
  {
    title: "7. Cookies and Analytics",
    blocks: [
      { p: "Our websites may use cookies, pixels, analytics tools and similar technologies." },
      { p: "These technologies may help us:" },
      { ul: [
        "operate our website;",
        "remember preferences;",
        "understand website traffic;",
        "measure marketing effectiveness;",
        "improve website functionality;",
        "detect security issues; and",
        "understand how visitors interact with our website.",
      ]},
      { p: "Third-party providers may also use cookies or similar technologies in connection with services they provide to us." },
      { p: "You may be able to control or disable cookies through your browser settings. Disabling certain cookies may affect website functionality." },
      { p: "Where required by applicable law, we will obtain consent before using non-essential cookies or similar technologies." },
    ],
  },
  {
    title: "8. Disclosure of Personal Information",
    blocks: [
      { p: "We may disclose personal information where reasonably necessary for the purposes described in this Privacy Policy." },
      { p: "Depending on the circumstances, information may be disclosed to:" },
      { ul: [
        "our employees and contractors;",
        "prospective employers and clients;",
        "recruitment partners;",
        "service providers;",
        "IT and software providers;",
        "cloud storage and hosting providers;",
        "customer relationship management providers;",
        "communication and email providers;",
        "recruitment and job platforms;",
        "payment providers;",
        "professional advisers;",
        "accountants, lawyers and auditors;",
        "insurers;",
        "referees where authorised;",
        "government agencies or regulators where required;",
        "law enforcement agencies where legally permitted or required;",
        "professional service providers; and",
        "other parties where you have provided consent or disclosure is otherwise permitted by law.",
      ]},
      { p: "We do not sell personal information as a standalone commercial product." },
    ],
  },
  {
    title: "9. Overseas Disclosure",
    blocks: [
      { p: "Synergy Outsourcing operates internationally and may use overseas employees, contractors and service providers." },
      { p: "As a result, personal information may be accessed, stored, processed or disclosed outside Australia." },
      { p: "Depending on the services we provide and the technology providers we use, information may be handled in countries including:" },
      { ul: [
        "Australia",
        "Pakistan",
        "United States",
        "Canada",
        "United Kingdom",
        "New Zealand",
        "countries where our technology, hosting or service providers operate.",
      ]},
      { p: "The countries involved may change from time to time as our service providers and technology arrangements change." },
      { p: "Where required by applicable Australian privacy law, we will take reasonable steps to ensure overseas recipients handle personal information appropriately and consistently with applicable privacy requirements." },
      { p: "Where we use overseas contractors or service providers to perform services involving personal information, we may require appropriate contractual confidentiality, privacy and security obligations." },
      { p: "Because Synergy operates internationally, information provided to us may therefore be accessible from or transferred to a country other than the country in which you are located." },
    ],
  },
  {
    title: "10. Security of Personal Information",
    blocks: [
      { p: "We take reasonable steps to protect personal information from:" },
      { ul: [
        "misuse;",
        "interference;",
        "loss;",
        "unauthorised access;",
        "unauthorised modification; and",
        "unauthorised disclosure.",
      ]},
      { p: "Depending on the nature of the information and circumstances, security measures may include:" },
      { ul: [
        "access controls;",
        "authentication requirements;",
        "role-based access;",
        "password protection;",
        "secure cloud services;",
        "encryption where appropriate;",
        "confidentiality obligations;",
        "employee and contractor controls;",
        "system monitoring;",
        "secure disposal practices; and",
        "incident response procedures.",
      ]},
      { p: "However, no method of electronic transmission, storage or processing is completely secure." },
      { p: "We cannot guarantee that personal information transmitted to or from our website or stored electronically will never be subject to unauthorised access." },
    ],
  },
  {
    title: "11. Data Retention",
    blocks: [
      { p: "We retain personal information only for as long as reasonably necessary for the purposes for which it was collected, including to:" },
      { ul: [
        "provide services;",
        "manage our business relationship with you;",
        "maintain appropriate business and financial records;",
        "comply with legal and regulatory obligations;",
        "resolve disputes;",
        "enforce agreements; and",
        "protect our legitimate business interests.",
      ]},
      { p: "Where personal information is no longer required and we are not required or authorised by law to retain it, we will take reasonable steps to destroy, delete or de-identify it." },
      { p: "Candidate information may be retained for a reasonable period so that we can contact candidates about future opportunities, where permitted by applicable law." },
    ],
  },
  {
    title: "12. Accuracy of Personal Information",
    blocks: [
      { p: "We take reasonable steps to ensure that personal information we collect, use or disclose is accurate, complete and up to date having regard to the purpose for which it is used." },
      { p: "You should notify us if your personal information changes or if you believe information we hold about you is inaccurate or incomplete." },
    ],
  },
  {
    title: "13. Access to Personal Information",
    blocks: [
      { p: "You may request access to personal information we hold about you, subject to applicable legal exceptions." },
      { p: "To request access, please contact us using the contact details at the end of this Privacy Policy." },
      { p: "We may need to verify your identity before providing access." },
      { p: "In certain circumstances, we may refuse access where permitted or required by law. If we refuse access, we will generally explain the reason unless we are not permitted to do so." },
    ],
  },
  {
    title: "14. Correction of Personal Information",
    blocks: [
      { p: "If you believe that personal information we hold about you is inaccurate, incomplete, out of date or misleading, you may request that we correct it." },
      { p: "We will take reasonable steps to correct information where appropriate." },
      { p: "You can request a correction by contacting us using the details below." },
    ],
  },
  {
    title: "15. Marketing Communications",
    blocks: [
      { p: "We may use personal information to communicate with you about our services, business updates, employment opportunities or other information that may be relevant to you, where permitted by applicable law." },
      { p: "Where required, we will obtain consent before sending direct marketing communications." },
      { p: "You may opt out of marketing communications at any time by:" },
      { ul: [
        "using the unsubscribe function included in the communication; or",
        "contacting us directly.",
      ]},
      { p: "If you opt out of marketing communications, we may still send you administrative, transactional or service-related communications where reasonably necessary." },
    ],
  },
  {
    title: "16. Third-Party Websites and Services",
    blocks: [
      { p: "Our website may contain links to websites, platforms or services operated by third parties." },
      { p: "Those websites operate independently of Synergy Outsourcing and may have their own privacy policies." },
      { p: "We are not responsible for the privacy practices, content or security of third-party websites." },
      { p: "You should review the privacy policy of a third-party website before providing personal information." },
    ],
  },
  {
    title: "17. Artificial Intelligence and Automated Processing",
    blocks: [
      { p: "We may use technology, including artificial intelligence or automated tools, to support activities such as:" },
      { ul: [
        "administrative processing;",
        "candidate matching;",
        "communications;",
        "document processing;",
        "workflow automation;",
        "data analysis;",
        "customer service;",
        "website functionality; and",
        "improving our services.",
      ]},
      { p: "Where AI or automated tools are used in connection with personal information, we will take reasonable steps to ensure their use is consistent with applicable privacy laws." },
      { p: "Where applicable privacy law requires additional notice, explanation, consent, human review or other safeguards in relation to automated decision-making, we will implement those requirements." },
      { p: "We do not intend for automated tools to replace appropriate human judgement where a decision could significantly affect an individual's rights or interests, unless permitted by applicable law and appropriate safeguards are in place." },
    ],
  },
  {
    title: "18. Children",
    blocks: [
      { p: "Our services are generally intended for businesses, workers, job applicants and adults." },
      { p: "We do not knowingly seek to collect personal information from children except where reasonably necessary and permitted by law." },
      { p: "If you believe a child has provided personal information to us without appropriate consent, please contact us so that we can review and, where appropriate, delete the information." },
    ],
  },
  {
    title: "19. Unsolicited Personal Information",
    blocks: [
      { p: "If we receive personal information that we did not solicit, we will assess whether we could have collected that information lawfully." },
      { p: "Where required by applicable privacy law, we will destroy or de-identify unsolicited personal information that we determine could not have been lawfully collected." },
    ],
  },
  {
    title: "20. Data Breaches",
    blocks: [
      { p: "We maintain procedures designed to identify, assess and respond to suspected data breaches." },
      { p: "If we become aware of a data breach involving personal information, we will assess the incident and take appropriate steps to contain, investigate and remediate it." },
      { p: "Where required by the Privacy Act 1988 (Cth) or other applicable law, we will notify affected individuals and the relevant regulator." },
      { p: "We may also take additional steps where we consider notification appropriate to protect affected individuals." },
    ],
  },
  {
    title: "21. Privacy Complaints",
    blocks: [
      { p: "If you have a concern about how we have handled your personal information or believe that we may have breached applicable privacy laws, please contact us." },
      { p: "We will:" },
      { ul: [
        "acknowledge your complaint where appropriate;",
        "investigate the issue;",
        "seek further information where necessary;",
        "take reasonable steps to resolve the complaint; and",
        "provide you with our response.",
      ]},
      { p: "We aim to respond to privacy complaints within a reasonable period." },
      { p: "If you are not satisfied with our response, you may contact the Office of the Australian Information Commissioner (OAIC)." },
      { p: "Further information about making a complaint to the OAIC is available at: https://www.oaic.gov.au/privacy/privacy-complaints" },
    ],
  },
  {
    title: "22. International Privacy Rights",
    blocks: [
      { p: "If you are located outside Australia, you may have additional privacy rights under the laws applicable to you." },
      { p: "Depending on your location, these may include rights relating to:" },
      { ul: [
        "access;",
        "correction;",
        "deletion;",
        "restriction of processing;",
        "objection;",
        "data portability; and",
        "withdrawal of consent.",
      ]},
      { p: "Where applicable law requires us to provide additional rights or protections, we will comply with those requirements to the extent they apply to our activities." },
    ],
  },
  {
    title: "23. Business Transfers",
    blocks: [
      { p: "If Synergy Outsourcing is involved in a merger, acquisition, restructuring, sale of assets or other corporate transaction, personal information may be transferred as part of that transaction." },
      { p: "Where appropriate, we will take reasonable steps to ensure that personal information continues to be protected." },
    ],
  },
  {
    title: "24. Changes to This Privacy Policy",
    blocks: [
      { p: "We may update this Privacy Policy from time to time to reflect changes to:" },
      { ul: [
        "our services;",
        "technology;",
        "business operations;",
        "privacy laws;",
        "regulatory requirements; or",
        "how we handle personal information.",
      ]},
      { p: 'The updated version will be published on our website with the relevant "Last updated" date.' },
      { p: "We encourage you to review this Privacy Policy periodically." },
    ],
  },
  {
    title: "25. Contact Us",
    blocks: [
      { p: "If you have questions about this Privacy Policy, wish to request access to or correction of your personal information, or wish to make a privacy complaint, please contact us." },
      { p: "Synergy Outsourcing Pty Ltd" },
      { p: "Email: info@synergyos.com.au" },
      { p: "Website: https://www.synergyos.com.au" },
      { p: "When contacting us about personal information, please provide sufficient information for us to identify you and understand your request." },
    ],
  },
  {
    title: "26. Applicable Law",
    blocks: [
      { p: "This Privacy Policy is intended to operate in accordance with applicable Australian privacy laws, including the Privacy Act 1988 (Cth) and the Australian Privacy Principles, where those laws apply to Synergy Outsourcing." },
      { p: "Where mandatory privacy laws in another jurisdiction apply to our processing of your personal information, those laws may provide additional rights or protections." },
    ],
  },
];

const TERMS_INTRO = [
  'These Website Terms and Conditions ("Terms") govern your access to and use of the Synergy Outsourcing website and related online services.',
  'The website is operated by Synergy Outsourcing Pty Ltd ("Synergy Outsourcing", "Synergy", "we", "us" or "our").',
  "By accessing or using our website, you agree to be bound by these Terms. If you do not agree with these Terms, you should not use our website.",
  "These Terms should be read together with our Privacy Policy, which explains how we collect, use, store and disclose personal information.",
];

const TERMS_SECTIONS = [
  {
    title: "1. About Synergy Outsourcing",
    blocks: [
      { p: "Synergy Outsourcing provides business outsourcing, recruitment, talent, administration, customer support, back-office, sales support and related business services." },
      { p: "Our website provides information about our services and may allow users to:" },
      { ul: [
        "learn about our services;",
        "submit business enquiries;",
        "request information or contact us;",
        "submit job applications;",
        "register as a candidate;",
        "submit resumes or other information;",
        "express interest in employment or contract opportunities; and",
        "interact with other features that we may make available from time to time.",
      ]},
      { p: "The information provided on the website is general information only and does not constitute an offer to provide services unless expressly stated otherwise." },
    ],
  },
  {
    title: "2. Acceptance of These Terms",
    blocks: [
      { p: "By accessing, browsing or using this website, you acknowledge that you have read, understood and agree to these Terms." },
      { p: "If you are using the website on behalf of a company, organisation or other entity, you represent that you have authority to accept these Terms on its behalf." },
      { p: "We may update these Terms from time to time. Your continued use of the website after updated Terms are published constitutes acceptance of the updated Terms." },
    ],
  },
  {
    title: "3. Website Use",
    blocks: [
      { p: "You may use our website for lawful purposes and in accordance with these Terms." },
      { p: "You must not:" },
      { ul: [
        "use the website for an unlawful or fraudulent purpose;",
        "interfere with or disrupt the website or its underlying systems;",
        "attempt to gain unauthorised access to our systems, accounts or data;",
        "introduce viruses, malware or other harmful code;",
        "scrape, crawl, copy or extract website content using automated systems without our permission;",
        "impersonate another person or organisation;",
        "submit false, misleading or fraudulent information;",
        "use the website to harass, abuse or harm another person;",
        "use information obtained through the website for unlawful purposes;",
        "attempt to circumvent website security measures;",
        "use the website to distribute spam or unsolicited communications;",
        "copy or reproduce our website or content for commercial purposes without our permission; or",
        "do anything that could damage, disable, overburden or impair the operation or security of the website.",
      ]},
      { p: "We may restrict or terminate access to the website if we reasonably believe that you have breached these Terms or are using the website in a way that may harm Synergy Outsourcing, another user or our systems." },
    ],
  },
  {
    title: "4. Website Information",
    blocks: [
      { p: "We aim to ensure that information published on our website is accurate and up to date." },
      { p: "However, we do not guarantee that all information is:" },
      { ul: ["complete;", "accurate;", "current;", "error-free; or", "suitable for your particular circumstances."] },
      { p: "Website content may change without notice." },
      { p: "Information about our services, pricing, capabilities, availability, candidates, positions or outcomes may also change from time to time." },
      { p: "You should contact us directly if you require specific information before making a business or commercial decision." },
    ],
  },
  {
    title: "5. No Guarantee of Employment or Recruitment Outcomes",
    blocks: [
      { p: "Information relating to employment opportunities, recruitment services or candidates is provided for general recruitment purposes." },
      { p: "Submitting an application or registering with Synergy does not guarantee:" },
      { ul: [
        "an interview;",
        "employment;",
        "contract engagement;",
        "placement;",
        "a particular salary or remuneration;",
        "selection by a client;",
        "continued consideration for a position; or",
        "any other employment outcome.",
      ]},
      { p: "Similarly, our recruitment and talent services do not guarantee that a client will successfully recruit a candidate or achieve a particular business outcome." },
      { p: "Final employment, engagement and hiring decisions are made by the relevant employer or client." },
    ],
  },
  {
    title: "6. Candidate Information",
    blocks: [
      { p: "If you submit a resume, application or other information to Synergy Outsourcing, you confirm that:" },
      { ul: [
        "the information you provide is accurate and not misleading;",
        "you have the right to provide the information;",
        "you have not knowingly provided information that infringes another person's rights;",
        "your qualifications, experience and employment history are represented honestly; and",
        "you will notify us if relevant information changes.",
      ]},
      { p: "You authorise Synergy Outsourcing to use information you provide for legitimate recruitment, employment and talent-related purposes in accordance with our Privacy Policy." },
      { p: "Where appropriate and permitted by applicable law, we may provide relevant candidate information to prospective employers or clients for recruitment purposes." },
    ],
  },
  {
    title: "7. Client and Business Enquiries",
    blocks: [
      { p: "If you submit an enquiry about our services, you agree to provide information that is accurate and sufficient for us to understand your requirements." },
      { p: "Submitting an enquiry does not create a contractual relationship between you and Synergy Outsourcing." },
      { p: "A contractual relationship will only arise when the relevant services are formally agreed between the parties, normally through a proposal, statement of work, service agreement, order form or other written agreement." },
      { p: "Where there is a conflict between these Website Terms and a separate written agreement between you and Synergy Outsourcing, the written agreement will generally take precedence for the services covered by that agreement." },
    ],
  },
  {
    title: "8. Pricing and Service Information",
    blocks: [
      { p: "Any prices, fees or service information displayed on our website are provided for general information unless expressly stated otherwise." },
      { p: "Prices may be:" },
      { ul: [
        "subject to change;",
        "exclusive of applicable taxes;",
        "dependent on the scope of services;",
        "dependent on location or service requirements; or",
        "subject to a separate written agreement.",
      ]},
      { p: "A price or service description on the website does not constitute a binding offer unless expressly stated otherwise." },
      { p: "Any applicable onboarding, recruitment, outsourcing, replacement, subscription or other fees will be governed by the relevant client agreement or proposal." },
    ],
  },
  {
    title: "9. Intellectual Property",
    blocks: [
      { p: "Unless otherwise stated, all content on this website is owned by or licensed to Synergy Outsourcing." },
      { p: "This includes:" },
      { ul: [
        "our business name and branding;",
        "logos;",
        "website design;",
        "text;",
        "graphics;",
        "photographs;",
        "videos;",
        "documents;",
        "templates;",
        "service descriptions;",
        "downloadable materials;",
        "software;",
        "layouts;",
        "databases; and",
        "other website content.",
      ]},
      { p: "Our intellectual property is protected by applicable copyright, trademark and other intellectual property laws." },
      { p: "You may access and view website content for your personal or internal business purposes." },
      { p: "You must not, without our prior written permission:" },
      { ul: [
        "reproduce substantial portions of the website;",
        "modify or republish our content;",
        "commercially exploit our content;",
        "sell or redistribute our materials;",
        "create derivative works from our content;",
        "use our branding or trademarks;",
        "reproduce our website design; or",
        "represent our content as your own.",
      ]},
      { p: "Nothing in these Terms transfers ownership of Synergy Outsourcing's intellectual property to you." },
    ],
  },
  {
    title: "10. User-Submitted Content",
    blocks: [
      { p: "You may have the opportunity to submit information, documents, resumes, comments, enquiries or other content through our website." },
      { p: "You retain ownership of your personal information and other content that you submit, subject to any rights you grant to us or our clients as part of providing our services." },
      { p: "By submitting content, you confirm that you have the right to provide that content and that doing so does not knowingly violate another person's rights." },
      { p: "You grant Synergy Outsourcing the rights reasonably necessary to receive, store, process and use submitted content for the purposes for which it was provided and in accordance with our Privacy Policy." },
      { p: "We may remove or refuse to publish content that we reasonably believe is unlawful, misleading, offensive, inappropriate, fraudulent or otherwise inconsistent with these Terms." },
    ],
  },
  {
    title: "11. Third-Party Services",
    blocks: [
      { p: "We may use third-party providers to operate or support our website and business." },
      { p: "These may include providers of:" },
      { ul: [
        "website hosting;",
        "cloud storage;",
        "email;",
        "analytics;",
        "customer relationship management;",
        "recruitment platforms;",
        "payment processing;",
        "communications;",
        "security;",
        "artificial intelligence;",
        "automation; and",
        "other technology services.",
      ]},
      { p: "Third-party services may have their own terms and privacy policies." },
      { p: "We are not responsible for the availability, functionality, security or privacy practices of third-party services to the extent permitted by law." },
    ],
  },
  {
    title: "12. Third-Party Links",
    blocks: [
      { p: "Our website may contain links to websites or services operated by third parties." },
      { p: "These links are provided for convenience and information only." },
      { p: "We do not control or endorse third-party websites unless expressly stated." },
      { p: "We are not responsible for:" },
      { ul: [
        "the content of third-party websites;",
        "their availability;",
        "their security;",
        "their privacy practices; or",
        "any loss or damage resulting from your use of those websites.",
      ]},
      { p: "You should review the terms and privacy policies applicable to third-party websites before using them." },
    ],
  },
  {
    title: "13. Artificial Intelligence and Technology",
    blocks: [
      { p: "Synergy Outsourcing may use artificial intelligence, automation and other technology in connection with our website and business operations." },
      { p: "These technologies may assist with:" },
      { ul: [
        "website functionality;",
        "communications;",
        "administration;",
        "document processing;",
        "candidate matching;",
        "recruitment workflows;",
        "data analysis;",
        "customer support;",
        "business operations; and",
        "service delivery.",
      ]},
      { p: "Technology-assisted processes may not always produce accurate or complete results." },
      { p: "Where appropriate, information generated or processed using automated systems may be reviewed by a human before being relied upon for a significant business or recruitment decision." },
      { p: "You should not assume that information generated by an automated system is necessarily complete or accurate without appropriate verification." },
    ],
  },
  {
    title: "14. Website Availability and Security",
    blocks: [
      { p: "We aim to keep our website available and secure, but we do not guarantee that it will:" },
      { ul: [
        "always be available;",
        "operate without interruption;",
        "be free from errors;",
        "be free from viruses or other harmful components; or",
        "remain unchanged.",
      ]},
      { p: "Website availability may be affected by maintenance, technical problems, hosting providers, telecommunications networks, cyber incidents or circumstances outside our reasonable control." },
      { p: "You are responsible for maintaining appropriate security on any device you use to access the website." },
    ],
  },
  {
    title: "15. Privacy",
    blocks: [
      { p: "Your use of our website is also subject to our Privacy Policy." },
      { p: "Our Privacy Policy explains how we collect, use, disclose, store and protect personal information." },
      { p: "By using the website or submitting information to us, you acknowledge that your information may be handled in accordance with that Privacy Policy." },
      { p: "You can review our Privacy Policy on our website." },
    ],
  },
  {
    title: "16. Confidentiality",
    blocks: [
      { p: "Information provided through the website should not be assumed to create a confidential relationship between you and Synergy Outsourcing unless we have expressly agreed otherwise in writing." },
      { p: "If you need to provide confidential commercial, technical or other sensitive information, we recommend contacting us first to establish appropriate confidentiality arrangements." },
      { p: "Nothing in these Terms overrides a separate confidentiality agreement or contractual obligation between you and Synergy Outsourcing." },
    ],
  },
  {
    title: "17. Limitation of Liability",
    blocks: [
      { p: "To the maximum extent permitted by law, Synergy Outsourcing excludes liability for any loss, damage, cost or expense arising directly or indirectly from:" },
      { ul: [
        "your use of or inability to use the website;",
        "reliance on information contained on the website;",
        "errors or omissions in website content;",
        "website interruptions or unavailability;",
        "third-party websites or services;",
        "unauthorised access to information;",
        "viruses or other harmful code;",
        "communications submitted through the website; or",
        "any other matter relating to your use of the website.",
      ]},
      { p: "This exclusion applies to direct, indirect, incidental, consequential and special loss, including loss of profits, revenue, business opportunities, data or anticipated savings, to the extent permitted by law." },
      { p: "Nothing in these Terms excludes, restricts or modifies any right, guarantee, warranty or remedy that cannot lawfully be excluded, restricted or modified." },
      { p: "Where the Australian Consumer Law or another mandatory law applies, our liability will be limited only to the extent permitted by that law." },
    ],
  },
  {
    title: "18. Consumer Guarantees",
    blocks: [
      { p: "Nothing in these Terms is intended to exclude, restrict or modify any rights or remedies that cannot legally be excluded under applicable law." },
      { p: "Where the Australian Consumer Law applies to our services, nothing in these Terms limits your rights under that legislation." },
      { p: "To the extent permitted by law, where a service is not ordinarily acquired for personal, domestic or household use, our liability may be limited in accordance with applicable legislation and the relevant written agreement." },
    ],
  },
  {
    title: "19. Indemnity",
    blocks: [
      { p: "To the maximum extent permitted by law, you agree to indemnify Synergy Outsourcing and its directors, officers, employees, contractors and service providers against losses, liabilities, claims, damages, costs and expenses arising from:" },
      { ul: [
        "your breach of these Terms;",
        "your unlawful use of the website;",
        "information or content you submit;",
        "your infringement of another person's intellectual property or other rights;",
        "your violation of applicable laws; or",
        "your misuse of the website or our services.",
      ]},
      { p: "This indemnity does not apply to the extent that the relevant loss was caused by Synergy Outsourcing's own negligence, unlawful conduct or other conduct for which liability cannot legally be excluded." },
    ],
  },
  {
    title: "20. Suspension and Termination",
    blocks: [
      { p: "We may suspend, restrict or terminate your access to all or part of the website at any time where reasonably necessary, including if:" },
      { ul: [
        "you breach these Terms;",
        "you use the website unlawfully;",
        "you compromise website security;",
        "your conduct creates a risk to Synergy Outsourcing or another user; or",
        "we discontinue or substantially change the website.",
      ]},
      { p: "We may also modify, suspend or discontinue any website feature or service without notice where reasonably necessary." },
      { p: "Termination of website access does not affect rights or obligations that have accrued before termination or provisions that are intended to survive termination." },
    ],
  },
  {
    title: "21. Complaints and Disputes",
    blocks: [
      { p: "If you have a concern about our website or services, we encourage you to contact us first so that we can attempt to resolve the issue." },
      { p: "Where you have entered into a separate agreement with Synergy Outsourcing, any dispute relating to the services will generally be handled in accordance with the dispute resolution provisions of that agreement." },
      { p: "Nothing in these Terms prevents you from exercising rights available to you under applicable law." },
    ],
  },
  {
    title: "22. Governing Law",
    blocks: [
      { p: "These Terms are governed by the laws of Victoria, Australia." },
      { p: "You agree that the courts of Victoria, Australia, and applicable courts exercising jurisdiction in Victoria, have jurisdiction in relation to disputes arising from or relating to these Terms, subject to any mandatory rights you may have under applicable law." },
      { p: "Where mandatory laws in another jurisdiction apply to you, those laws may provide you with additional rights that cannot be excluded by these Terms." },
    ],
  },
  {
    title: "23. Changes to These Terms",
    blocks: [
      { p: "We may change these Terms from time to time." },
      { p: "Changes will become effective when the updated Terms are published on our website unless a different effective date is stated." },
      { p: 'The "Last updated" date at the beginning of these Terms indicates when they were most recently updated.' },
      { p: "Your continued use of the website after changes are published constitutes acceptance of the updated Terms to the extent permitted by law." },
    ],
  },
  {
    title: "24. Severability",
    blocks: [
      { p: "If any provision of these Terms is found to be invalid, unlawful or unenforceable, that provision will be read down or severed to the extent necessary." },
      { p: "The remaining provisions will continue to operate to the extent permitted by law." },
    ],
  },
  {
    title: "25. No Waiver",
    blocks: [
      { p: "If we do not immediately enforce a right or provision under these Terms, this does not mean that we have waived that right or provision." },
      { p: "Any waiver must be expressly given by Synergy Outsourcing." },
    ],
  },
  {
    title: "26. Entire Agreement",
    blocks: [
      { p: "These Terms govern your use of the Synergy Outsourcing website." },
      { p: "They do not replace or override any separate written agreement that you may have entered into with Synergy Outsourcing for specific services." },
      { p: "Where a separate written agreement applies to a particular service, that agreement will govern the relevant commercial relationship to the extent of any inconsistency." },
    ],
  },
  {
    title: "27. Contact Us",
    blocks: [
      { p: "If you have any questions about these Terms, please contact us." },
      { p: "Synergy Outsourcing Pty Ltd" },
      { p: "Email: info@synergyos.com.au" },
      { p: "Website: https://www.synergyos.com.au" },
    ],
  },
];

const LegalLayout = ({ intro, sections, updatedNote }) => (
  <section className="pb-16 pt-6">
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
      <div className="space-y-3 text-sm leading-relaxed text-mist">
        {intro.map((p) => (
          <p key={p.slice(0, 40)}>{p}</p>
        ))}
      </div>
      {sections.map((s) => (
        <Sec key={s.title} title={s.title} blocks={s.blocks} />
      ))}
      <p className="mt-12 border-t border-white/10 pt-6 font-mono text-xs uppercase tracking-[0.15em] text-slate-500">
        {updatedNote}
      </p>
    </div>
  </section>
);

export function PrivacyPage() {
  useSEO(
    "Privacy Policy | Synergy Outsourcing",
    "How Synergy Outsourcing Pty Ltd collects, uses, discloses, stores and protects personal information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles."
  );
  return (
    <>
      <PageHero eyebrow="Legal" lines={["Privacy Policy"]} copy="Last updated: 6 October 2026" />
      <div data-testid="privacy-content">
        <LegalLayout intro={PRIVACY_INTRO} sections={PRIVACY_SECTIONS} updatedNote="Last updated: 6 October 2026" />
      </div>
    </>
  );
}

export function TermsPage() {
  useSEO(
    "Terms and Conditions | Synergy Outsourcing",
    "Website Terms and Conditions governing access to and use of the Synergy Outsourcing website and related online services."
  );
  return (
    <>
      <PageHero eyebrow="Legal" lines={["Website Terms and Conditions"]} copy="Last updated: 6 October 2026" />
      <div data-testid="terms-content">
        <LegalLayout intro={TERMS_INTRO} sections={TERMS_SECTIONS} updatedNote="Last updated: 6 October 2026" />
      </div>
    </>
  );
}
